import date from '@lib/date'
import fareFeature from '@lib/fareFeature'
import { t } from '@lib/i18n'

const MINUTES_A_DAY = 1440

const minutesIn: Record<string, number> = {
  days: 24 * 60,
  hours: 60,
  minutes: 1,
}

export const getFareFeatureMessage = (fareFeatures: FareFeature): string | null => {
  switch (true) {
    case fareFeature.isFullyRefundable(fareFeatures):
      return t('checkout.tripDetails.refundable.full')
    case fareFeature.isPartiallyRefundable(fareFeatures):
      return t('checkout.tripDetails.refundable.partial')
    case fareFeature.isNotRefundable(fareFeatures):
      return t('checkout.tripDetails.refundable.not')
    case fareFeature.isVoucherRefund(fareFeatures):
      return t('checkout.tripDetails.refundable.voucher')
    case fareFeature.isAmendable(fareFeatures):
      return t('checkout.tripDetails.amendable.full')
    case fareFeature.isNonAmendable(fareFeatures):
      return t('checkout.tripDetails.amendable.not')
    case fareFeature.isMobileWalletOnly(fareFeatures):
      return t('checkout.tripDetails.mobileWalletOnly')
  }

  return null
}

export const checkIsLimitedByDailyUsageRule = (rule: TicketValidityRule): boolean => {
  const { durationType, duration, referenceTimeType } = rule

  if (referenceTimeType !== 'beginning_of_departure_day') {
    return false
  }

  const minutes = minutesIn[durationType] ?? 0

  const durationInMinutes = duration * minutes

  return durationInMinutes === MINUTES_A_DAY
}

export const checkIsLimitedOneTimeUseRule = (rule?: TicketValidityRule): boolean =>
  !!(
    rule &&
    ['beginning_of_departure_day', 'beginning_of_purchase_day'].includes(rule.referenceTimeType) &&
    rule.useType === 'once' &&
    rule.duration > 0
  )

export const checkIsDailyPassTicket = (fareClass?: FareClass, type: ConnectionType = 'outbound'): boolean =>
  !!(
    fareClass &&
    fareClass.journeyType === 'open' &&
    fareClass.ticketValidityRules?.some(rule => rule.useType === 'multiple' && rule.ruleType === type)
  )

export const checkIsLimitedOneTimeUseTicket = (fareClass?: FareClass): boolean =>
  !!(fareClass && fareClass.ticketValidityRules?.some(checkIsLimitedOneTimeUseRule))

export const getValidityMessage = (fareClass?: FareClass, type?: ConnectionType): string | null => {
  const rule = fareClass?.ticketValidityRules?.find(r => r.ruleType === type) || null

  if (!rule?.duration) return null

  const duration = date.formatDuration({ [rule.durationType]: rule.duration })

  return t('checkout.tripDetails.available', { duration, count: Number(checkIsLimitedByDailyUsageRule(rule)) })
}

export const getValidity = (carriers: BaseCarrier[], fareClass?: string, type?: ConnectionType): string | null => {
  const messages = carriers.map(carrier => {
    const fare = carrier.fareClasses.find(item => item.code === fareClass)

    return getValidityMessage(fare, type)
  })

  return [...new Set(messages)].length === 1 ? messages[0] : null
}
